import Img from "next/image";
import type { JSX } from "react";
import { useMemo } from "react";
import cn from "@/utils/cn";
import type ImageProps from "./types";
import { ImageSizes } from "./types";

const prefixes = ["min-h", "min-w", "max-h", "max-w", "h", "w"];
// Crear regex para capturar prefix y suffix
const sizeRegex = new RegExp(String.raw`(${prefixes.join("|")})-(\w+)`, "g");

/**
 * @name Image
 * @description Renders an optimized Image component using Next.js Image.
 * Supports dynamic sizing mapping via `className` (e.g., `w-sm`, `h-lg`) based on `ImageSizes`.
 *
 * @param {ImageProps} props - Component properties.
 * @param {string} [props.alt="image"] - Alternative text for accessibility.
 * @param {number} [props.width=100] - Default width in pixels (can be overridden by classname).
 * @param {number} [props.height=100] - Default height in pixels (can be overridden by classname).
 * @param {string} [props.src] - Image source URL.
 * @param {number} [props.quality=75] - Image quality (1-100).
 * @returns {JSX.Element} The rendered optimized image.
 * @see https://nextjs.org/docs/pages/api-reference/components/image
 */
const Image = ({
  className = "",
  loading,
  width = 50,
  height = 50,
  quality = 75,
  ...rest
}: ImageProps): JSX.Element => {
  const { w, h, clsName } = useMemo(() => {
    let w = width;
    let h = height;
    const classNameString = cn(className);

    for (const match of classNameString.matchAll(sizeRegex)) {
      const prefix = match[1];
      const suffix = match[2];
      if (suffix in ImageSizes) {
        const size = ImageSizes[suffix as keyof typeof ImageSizes];
        if (prefix.includes("w")) w = size;
        if (prefix.includes("h")) h = size;
      }
    }
    return { w, h, clsName: classNameString };
  }, [className, height, width]);

  return (
    <Img
      width={w}
      height={h}
      className={clsName}
      quality={quality}
      loading={loading ?? "lazy"}
      decoding="async"
      placeholder="empty"
      {...rest}
    />
  );
};

export default Image;
